import { ParentTypes } from '@risksmart-app/domain/src/types/consts';
import type { GetOversightResultsByParentIdResponse } from '@risksmart-app/trpc/types';
import type { GetOversightResultsByParentIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetOversightResultsByParentIdDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook } from 'src/utils';

type UseGetOversightResultsByParentIdArgs = { parentId: string };

/**
 * Maps tRPC oversight-results-by-parent data to the legacy GraphQL query shape.
 *
 * The 5 oversight-result legs each carry a single `parents` array (ParentId /
 * ParentType / entity / node / oversightAssessment). The GraphQL query wants
 * `parents` filtered to the entity's own ParentType and projected to
 * `{ entity, node }`. The `__typename` on each of those legs is load-bearing:
 * `oversightRatingRegisterUtils.getOversightResultTableFields` dispatches off
 * it. The `control_test` leg keeps `parent` (not `parents`) as-is.
 *
 * The `issue.actions_aggregate` and `action.updates_aggregate` counts are not
 * present as aggregates in the tRPC rows — they are computed here: the issue
 * config selects `actions` pre-filtered to Status=Open (so `actions.length` is
 * the open-action count the GraphQL aggregate exposed), and the action config
 * carries the total via the `actionUpdateSummary` view's `Count`.
 */
export const mapTrpcOversightResultsByParentIdToGraphQL = (
  trpcData: GetOversightResultsByParentIdResponse
): GetOversightResultsByParentIdQuery => ({
  document_oversight_result: trpcData.document_oversight_result.map((row) => ({
    ...row,
    __typename: 'document_oversight_result',
    parents: row.parents
      .filter((p) => p.ParentType === ParentTypes.Document)
      .map((p) => ({ document: p.document, node: p.node })),
  })),
  obligation_oversight_result: trpcData.obligation_oversight_result.map(
    (row) => ({
      ...row,
      __typename: 'obligation_oversight_result',
      parents: row.parents
        .filter((p) => p.ParentType === ParentTypes.Obligation)
        .map((p) => ({ obligation: p.obligation, node: p.node })),
    })
  ),
  risk_controlled_oversight_result:
    trpcData.risk_controlled_oversight_result.map((row) => ({
      ...row,
      __typename: 'risk_controlled_oversight_result',
      parents: row.parents
        .filter((p) => p.ParentType === ParentTypes.Risk)
        .map((p) => ({ risk: p.risk, node: p.node })),
    })),
  risk_uncontrolled_oversight_result:
    trpcData.risk_uncontrolled_oversight_result.map((row) => ({
      ...row,
      __typename: 'risk_uncontrolled_oversight_result',
      parents: row.parents
        .filter((p) => p.ParentType === ParentTypes.Risk)
        .map((p) => ({ risk: p.risk, node: p.node })),
    })),
  control_test_oversight_result: trpcData.control_test_oversight_result.map(
    (row) => ({
      ...row,
      __typename: 'control_test_oversight_result',
    })
  ),
  impact_oversight_rating: trpcData.impact_oversight_rating,
  issue: trpcData.issue.map((row) => ({
    ...row,
    actions_aggregate: {
      aggregate: { count: row.actions.length },
    },
  })),
  impact: trpcData.impact,
  action: trpcData.action.map((row) => ({
    ...row,
    updates_aggregate: {
      aggregate: { count: row.actionUpdateSummary?.Count ?? 0 },
    },
  })),
});

export const useGetOversightResultsByParentId = createQueryHook<
  UseGetOversightResultsByParentIdArgs,
  GetOversightResultsByParentIdResponse,
  GetOversightResultsByParentIdQuery
>({
  trpcQueryOptions: (trpc, { parentId }) =>
    trpc.frontend.assessment.getOversightResultsByParentId.queryOptions({
      parentId,
    }),
  mapTrpcDataToGraphQL: mapTrpcOversightResultsByParentIdToGraphQL,
  graphqlDocument: GetOversightResultsByParentIdDocument,
  graphqlVariables: ({ parentId }) => ({ ParentId: parentId }),
});
